import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

type ThemeMode = "light" | "dark";

interface ThemeContextType {
  themeMode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    if (typeof window !== "undefined") {
      const localTheme = window.localStorage.getItem("theme");
      return (localTheme === "dark" || localTheme === "light") ? localTheme : "light";
    }
    return "light";
  });

  const toggleTheme = useCallback(() => {
    setThemeMode((currentTheme) => {
      const newTheme = currentTheme === "light" ? "dark" : "light";
      window.localStorage.setItem("theme", newTheme);
      // Synchronize with Chakra UI color mode
      window.localStorage.setItem("chakra-ui-color-mode", newTheme);
      return newTheme;
    });
  }, []);

  // Initialize Chakra UI color mode on mount
  useEffect(() => {
    const chakraMode = window.localStorage.getItem("chakra-ui-color-mode");
    if (!chakraMode || chakraMode !== themeMode) {
      window.localStorage.setItem("chakra-ui-color-mode", themeMode);
    }
  }, [themeMode]);

  return (
    <ThemeContext.Provider value={{ themeMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useThemeMode(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useThemeMode must be used within a ThemeProvider");
  }
  return context;
}

